import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { Navbar } from "../components/Navbar";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { bandColor } from "../lib/bands";
import { ArrowUpRight, Sparkles, BellRing, CalendarClock, TrendingUp, TrendingDown } from "lucide-react";

export default function Dashboard() {
  const { user } = useAuth();
  const [items, setItems] = useState(null);
  const [reminder, setReminder] = useState(null);

  useEffect(() => {
    api.get("/assessments").then(({ data }) => setItems(data)).catch(() => setItems([]));
    api.get("/reminder").then(({ data }) => setReminder(data)).catch(() => {});
  }, []);

  const latest = items && items.length ? items[items.length - 1] : null;
  const chartData = (items || []).map((a, i) => ({
    name: `#${i + 1}`,
    core: a.scores.core,
    projected: a.projected_core ?? a.scores.core,
    date: new Date(a.created_at).toLocaleDateString(),
  }));

  return (
    <div className="min-h-screen grain">
      <Navbar />
      <div className="max-w-6xl mx-auto px-5 md:px-8 py-12 relative z-10">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-10">
          <div>
            <span className="label-tiny">Dashboard</span>
            <h1 className="font-display font-extrabold text-3xl md:text-4xl tracking-tight mt-2 text-ink">
              Hi{user?.name ? `, ${user.name.split(" ")[0]}` : ""}.
            </h1>
          </div>
          <Link to="/survey" data-testid="dashboard-new" className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-brand-red text-white font-semibold hover:bg-[#e63f3f] transition-colors duration-300">
            <Sparkles size={16} /> New assessment
          </Link>
        </div>

        {reminder && reminder.has_assessment && (
          reminder.due ? (
            <div data-testid="reminder-banner-due" className="mb-6 rounded-3xl border border-brand-orange/40 bg-gradient-to-r from-[#FFF3E4] to-[#FDFBF7] p-6 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div className="flex items-start gap-3">
                <BellRing size={20} className="text-brand-orange mt-0.5 shrink-0" />
                <div>
                  <p className="font-display font-bold text-lg text-ink">Time for a check-in</p>
                  <p className="text-sm text-stone mt-1">It's been {reminder.days_since} days since your last assessment. Retake it to see if your score actually moved.</p>
                </div>
              </div>
              <Link to="/survey" data-testid="reminder-retake" className="shrink-0 inline-flex items-center gap-2 px-6 py-3 rounded-full bg-brand-orange text-white font-semibold hover:bg-[#e67e00] transition-colors duration-300">
                Retake now <ArrowUpRight size={16} />
              </Link>
            </div>
          ) : (
            <div data-testid="reminder-banner-scheduled" className="mb-6 rounded-2xl border border-clay bg-white px-5 py-3 flex items-center gap-3">
              <CalendarClock size={16} className="text-stone" />
              <p className="text-sm text-stone">Next check-in in <span className="text-ink font-mono">{reminder.due_in_days}</span> days — reassess every {reminder.cadence} days to track real progress.</p>
            </div>
          )
        )}

        {items === null ? (
          <p className="text-stone font-mono text-sm">Loading…</p>
        ) : items.length === 0 ? (
          <div className="card-soft p-12 text-center">
            <h2 className="font-display font-bold text-xl mb-2 text-ink">No scores yet</h2>
            <p className="text-stone mb-6">Take your first 4-minute survey to generate your Scorecaster Score.</p>
            <Link to="/survey" className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-brand-red text-white font-semibold hover:bg-[#e63f3f] transition-colors duration-300">
              Start now <ArrowUpRight size={16} />
            </Link>
          </div>
        ) : (
          <>
            <div className="grid lg:grid-cols-3 gap-6 mb-6">
              <div className="card-soft p-6">
                <span className="label-tiny">Latest core score</span>
                <div className="font-mono text-6xl font-extrabold mt-3" style={{ color: bandColor(latest.scores.band) }}>{latest.scores.core}</div>
                <span className="label-tiny" style={{ color: bandColor(latest.scores.band) }}>{latest.scores.band}</span>
                {reminder && reminder.movement != null && (
                  <div data-testid="score-movement" className={`mt-3 inline-flex items-center gap-1.5 text-sm font-mono ${reminder.movement >= 0 ? "text-moss" : "text-[#B0554D]"}`}>
                    {reminder.movement >= 0 ? <TrendingUp size={15} /> : <TrendingDown size={15} />}
                    {reminder.movement >= 0 ? "+" : ""}{reminder.movement} vs last check-in
                  </div>
                )}
                {latest.gained_points > 0 && (
                  <div className="mt-4 pt-4 border-t border-clay" data-testid="dashboard-projected">
                    <span className="label-tiny">Projected with committed actions</span>
                    <div className="flex items-baseline gap-2 mt-1">
                      <span className="font-mono text-2xl font-bold" style={{ color: bandColor(latest.projected_band) }}>{latest.projected_core}</span>
                      <span className="font-mono text-xs text-moss">▲ {latest.gained_points}</span>
                    </div>
                  </div>
                )}
              </div>
              <div className="lg:col-span-2 card-soft p-6">
                <span className="label-tiny">Score trajectory</span>
                <div className="h-44 mt-4" data-testid="score-chart">
                  {chartData.length < 2 ? (
                    <div className="h-full flex items-center justify-center text-center px-4">
                      <p className="text-sm text-stone" data-testid="chart-empty-hint">Take another assessment to see your trajectory build over time.</p>
                    </div>
                  ) : (
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={chartData} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
                        <CartesianGrid stroke="rgba(140,120,90,0.12)" vertical={false} />
                        <XAxis dataKey="name" stroke="#A79E92" fontSize={11} tickLine={false} axisLine={false} />
                        <YAxis domain={[0, 100]} stroke="#A79E92" fontSize={11} tickLine={false} axisLine={false} />
                        <Tooltip contentStyle={{ background: "#ffffff", border: "1px solid #EADDCE", borderRadius: 12, fontSize: 12 }} labelStyle={{ color: "#6B655F" }} />
                        <Line type="monotone" dataKey="projected" name="Projected" stroke="#5C6B4E" strokeWidth={1.5} strokeDasharray="4 4" dot={false} />
                        <Line type="monotone" dataKey="core" name="Core" stroke="#FF8A00" strokeWidth={2.5} dot={{ r: 3, fill: "#FF4D4D" }} />
                      </LineChart>
                    </ResponsiveContainer>
                  )}
                </div>
              </div>
            </div>

            <span className="label-tiny">History</span>
            <div className="mt-4 space-y-3">
              {[...items].reverse().map((a) => (
                <Link key={a.id} to={`/results/${a.id}`} data-testid={`history-item-${a.id}`}
                  className="flex items-center justify-between gap-4 card-soft px-5 py-4 hover:-translate-y-0.5 transition-transform duration-500">
                  <div className="flex items-center gap-4">
                    <span className="font-mono text-2xl font-bold" style={{ color: bandColor(a.scores.band) }}>{a.scores.core}</span>
                    <div>
                      <p className="text-sm text-ink">{a.fit.decision_label}</p>
                      <span className="text-xs text-stone">{new Date(a.created_at).toLocaleDateString()} · fit {a.fit.fit} · {a.fit.probability}</span>
                    </div>
                  </div>
                  <ArrowUpRight size={18} className="text-stone" />
                </Link>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
